import { ReactNode } from "react"
import { Button } from "@/components/ui/button"
import { Loader2, AlertTriangle, ArrowRight } from "lucide-react"
import { cn } from "@/lib/utils"

/**
 * Primary action row — the headline action for a stage.
 * One big button with count, optional secondary content (e.g. slider).
 */
export function PrimaryActionRow({
  label,
  count,
  helpText,
  buttonLabel,
  onClick,
  disabled,
  loading,
  variant = "primary",
  children,
}: {
  label: string
  count?: number
  helpText?: string
  buttonLabel: string
  onClick: () => void | Promise<void>
  disabled?: boolean
  loading?: boolean
  variant?: "primary" | "ghost"
  children?: ReactNode
}) {
  const isDisabled = disabled || loading || count === 0
  return (
    <div className="rounded-xl border bg-card p-4 space-y-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            {count !== undefined && (
              <span className="text-2xl font-bold tabular-nums text-foreground">{count}</span>
            )}
            <span className="text-sm font-medium text-foreground">{label}</span>
          </div>
          {helpText && <p className="text-xs text-muted-foreground mt-1">{helpText}</p>}
        </div>
        <Button
          size="sm"
          disabled={isDisabled}
          onClick={onClick}
          className={cn(
            "shrink-0 h-9 w-full sm:w-auto",
            variant === "primary" &&
              !isDisabled &&
              "bg-primary hover:bg-primary-hover text-white",
            variant === "ghost" && "bg-transparent border border-input hover:bg-accent",
          )}
        >
          {loading && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
          {buttonLabel}
        </Button>
      </div>
      {children}
    </div>
  )
}

/**
 * Sub-stage row — for mid-stage steps (e.g. "7 awaiting approval" inside Care Plan).
 * Smaller than PrimaryActionRow, used as an inline list inside a stage.
 */
export function SubStageRow({
  label,
  count,
  helpText,
  actionLabel,
  onAction,
  loading,
  tone = "neutral",
}: {
  label: string
  count: number
  helpText?: string
  actionLabel?: string
  onAction?: () => void | Promise<void>
  loading?: boolean
  tone?: "neutral" | "running" | "warning" | "success"
}) {
  const toneColors: Record<string, { dot: string; text: string }> = {
    neutral: { dot: "bg-muted-foreground/40", text: "text-foreground" },
    running: { dot: "bg-info animate-pulse", text: "text-info" },
    warning: { dot: "bg-warning", text: "text-warning" },
    success: { dot: "bg-success", text: "text-success" },
  }
  const t = toneColors[tone]
  const isDisabled = count === 0 || loading || !onAction
  return (
    <div className="flex items-center gap-3 py-2">
      <span className={cn("h-2 w-2 rounded-full shrink-0", t.dot)} />
      <div className="flex-1 min-w-0 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className={cn("text-lg font-semibold tabular-nums", t.text)}>{count}</span>
        <span className="text-sm text-foreground">{label}</span>
        {helpText && <span className="text-xs text-muted-foreground">· {helpText}</span>}
      </div>
      {actionLabel && onAction && (
        <Button
          variant="ghost"
          size="sm"
          disabled={isDisabled}
          onClick={onAction}
          className="h-7 text-xs gap-1 shrink-0"
        >
          {loading ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <>
              {actionLabel}
              <ArrowRight className="h-3 w-3" />
            </>
          )}
        </Button>
      )}
    </div>
  )
}

/**
 * Blocker alert — red/amber box inside a stage describing a problem that's
 * preventing patients from progressing + optional CTA to fix it.
 */
export function BlockerAlert({
  severity = "warning",
  title,
  description,
  actionLabel,
  onAction,
}: {
  severity?: "warning" | "error"
  title: string
  description?: string
  actionLabel?: string
  onAction?: () => void
}) {
  const colors =
    severity === "error"
      ? "border-destructive/30 bg-destructive/10"
      : "border-warning/30 bg-warning/10"
  const iconColor = severity === "error" ? "text-destructive" : "text-warning"
  return (
    <div className={cn("rounded-xl border p-3.5 flex items-start gap-3", colors)}>
      <AlertTriangle className={cn("h-4 w-4 mt-0.5 shrink-0", iconColor)} />
      <div className="flex-1 min-w-0">
        <p className={cn("text-sm font-semibold", iconColor)}>{title}</p>
        {description && (
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{description}</p>
        )}
      </div>
      {actionLabel && onAction && (
        <Button variant="outline" size="sm" onClick={onAction} className="shrink-0 h-7 text-xs">
          {actionLabel}
        </Button>
      )}
    </div>
  )
}
